import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  Animated,
  Pressable,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { LucideIcon } from "lucide-react-native";

const TOAST_HEIGHT = 74;

interface BannerButton {
  text: string;
  onPress: () => void;
  variant: "primary" | "secondary";
  disabled?: boolean;
  isLoading?: boolean;
}

interface AnimatedBottomBannerProps {
  visible: boolean;
  icon?: LucideIcon;
  message: string;
  buttons?: BannerButton[];
  colorScheme?: "main" | "secondary";
  position?: "top" | "bottom";
  className?: string;
}

const AnimatedBottomBanner = ({
  visible,
  icon: Icon,
  message,
  buttons,
  colorScheme = "main",
  position = "bottom",
  className,
}: AnimatedBottomBannerProps) => {
  const { height: SCREEN_HEIGHT } = Dimensions.get("window");
  const translateY = useRef(
    new Animated.Value(position === "top" ? -TOAST_HEIGHT : SCREEN_HEIGHT)
  ).current;

  useEffect(() => {
    if (visible) {
      // Reset position before animating in
      translateY.setValue(position === "top" ? -TOAST_HEIGHT : TOAST_HEIGHT);

      // Animate in
      Animated.spring(translateY, {
        toValue: position === "top" ? 99 : 0,
        useNativeDriver: true,
        tension: 50,
        friction: 7,
      }).start();
    } else {
      // Animate out
      Animated.timing(translateY, {
        toValue: position === "top" ? -TOAST_HEIGHT : TOAST_HEIGHT,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, position]);

  return (
    <View
      className={`absolute ${
        position === "top" ? "top-0" : "bottom-0"
      } w-full items-center bg-transparent z-50 ${className}`}
    >
      <Animated.View
        style={{
          transform: [{ translateY }],
          height: translateY.interpolate({
            inputRange: [0, TOAST_HEIGHT],
            outputRange: [TOAST_HEIGHT, 0],
          }),
          width: "calc(100% - 8px)",
          marginBottom: position === "bottom" && 4,
          maxWidth: 624,
        }}
        className="bg-transparent"
      >
        <View
          className={`px-3 py-[14px] ${
            colorScheme === "main"
              ? "bg-blue-50 border-blue-500"
              : "bg-gray-50 border-gray-500"
          } border rounded-md shadow-md`}
        >
          <View className="flex-row items-center">
            {Icon && (
              <Icon
                size={20}
                className={`${
                  colorScheme === "main" ? "text-blue-800" : "text-gray-800"
                } max-[334px]:hidden`}
              />
            )}
            <View className="flex-1 flex-row items-center justify-between ml-3">
              <Text
                className={`text-sm font-semibold ${
                  colorScheme === "main" ? "text-blue-800" : "text-gray-800"
                } mr-2`}
              >
                {message}
              </Text>
              {buttons && buttons.length > 0 && (
                <View className="flex-row gap-2">
                  {buttons.map((button, index) => (
                    <Pressable
                      key={index}
                      onPress={button.onPress}
                      className={`rounded-md justify-center items-center ${
                        button.variant === "primary"
                          ? colorScheme === "main"
                            ? "bg-blue-500 hover:bg-blue-600"
                            : "bg-gray-500 hover:bg-gray-600"
                          : "border border-red-400 bg-white"
                      } px-3 py-1`}
                      disabled={button.disabled}
                    >
                      {button.isLoading ? (
                        <View className="w-[32px] items-center">
                          <ActivityIndicator size="small" color="#FFFFFF" />
                        </View>
                      ) : (
                        <Text
                          className={`text-sm font-semibold ${
                            button.variant === "primary"
                              ? "text-white"
                              : "text-red-500"
                          }`}
                        >
                          {button.text}
                        </Text>
                      )}
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </View>
        </View>
      </Animated.View>
    </View>
  );
};

export default AnimatedBottomBanner;
